import Image from "next/image";
import { Fragment } from "react";

import { UPLink } from "./up-link";

import type { LinkField } from "@prismicio/client";
import type { RichTextComponents } from "@prismicio/react";
import type { PropsWithChildren } from "react";

export const unstyledRichTextComponents = function (): RichTextComponents {
  return {
    heading1({ children, key }: PropsWithChildren<{ key: string }>) {
      return <h1 key={key}>{children}</h1>;
    },

    heading2({ children, key }: PropsWithChildren<{ key: string }>) {
      return <h2 key={key}>{children}</h2>;
    },

    heading3({ children, key }: PropsWithChildren<{ key: string }>) {
      return <h3 key={key}>{children}</h3>;
    },

    heading4({ children, key }: PropsWithChildren<{ key: string }>) {
      return <h4 key={key}>{children}</h4>;
    },

    heading5({ children, key }: PropsWithChildren<{ key: string }>) {
      return <h5 key={key}>{children}</h5>;
    },

    heading6({ children, key }: PropsWithChildren<{ key: string }>) {
      return <h6 key={key}>{children}</h6>;
    },

    paragraph({ children, key }: PropsWithChildren<{ key: string }>) {
      return <p key={key}>{children}</p>;
    },

    preformatted({ children, key }: PropsWithChildren<{ key: string }>) {
      return <pre key={key}>{children}</pre>;
    },

    strong({ children, key }: PropsWithChildren<{ key: string }>) {
      return <strong key={key}>{children}</strong>;
    },

    em({ children, key }: PropsWithChildren<{ key: string }>) {
      return <em key={key}>{children}</em>;
    },

    listItem({ children, key }: PropsWithChildren<{ key: string }>) {
      return <li key={key}>{children}</li>;
    },

    list({ children, key }: PropsWithChildren<{ key: string }>) {
      return <ul key={key}>{children}</ul>;
    },

    oListItem({ children, key }: PropsWithChildren<{ key: string }>) {
      return <li key={key}>{children}</li>;
    },

    oList({ children, key }: PropsWithChildren<{ key: string }>) {
      return <ol key={key}>{children}</ol>;
    },

    image: ({
      node,
      key,
    }: {
      key: string;
      node: {
        url: string;
        dimensions: { width: number; height: number };
        alt: string | null;
        linkTo?: LinkField | null;
      };
    }) => {
      const img = (
        <Image
          key={key}
          src={node.url}
          width={node.dimensions.width}
          height={node.dimensions.height}
          alt={node.alt || ""}
          quality={55}
          loading="lazy"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />
      );

      return (
        <p key={key} className="block-img">
          {node.linkTo ? <UPLink field={node.linkTo}>{img}</UPLink> : img}
        </p>
      );
    },

    hyperlink: (
      props: PropsWithChildren<{
        key: string;
        node: { data: LinkField };
      }>,
    ) => {
      const { node, children, key } = props;
      const data = node.data as { target?: string };
      const target = data.target;
      return (
        <UPLink
          key={key}
          field={node.data}
          rel={target ? "noopener noreferrer" : undefined}
          target={target || "_self"}
        >
          {children}
        </UPLink>
      );
    },

    embed: ({ node, key }) => (
      <div
        key={key}
        data-oembed={node.oembed.embed_url}
        data-oembed-type={node.oembed.type}
        data-oembed-provider={node.oembed.provider_name}
        dangerouslySetInnerHTML={{ __html: node.oembed.html ?? "" }}
      />
    ),

    label: ({
      node,
      children,
      key,
    }: PropsWithChildren<{
      key: string;
      node: { data: { label: string } };
    }>) => (
      <span key={key} className={node.data.label}>
        {children}
      </span>
    ),

    span: (
      props: PropsWithChildren<{
        key: string;
        text: string;
      }>,
    ) => {
      const { text, key } = props;
      const result = [];

      let i = 0;
      for (const line of text.split("\n")) {
        if (i > 0) {
          result.push(<br key={`${i}__break`} />);
        }

        result.push(<Fragment key={`${i}__line`}>{line}</Fragment>);

        i++;
      }

      return <Fragment key={key}>{result}</Fragment>;
    },
  };
};
